/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as Sentry from '@sentry/browser';
import * as SentryTypes from '@sentry/types';
import { getConfig } from '@utils/configs';
import { pick, omit, includes } from '@utils/lodash';

// Sets user information on the Sentry instance. Changes here will impact all events reported by the Sentry project.
// User info is specifically grouped under the user object in bug reports.
// istanbul ignore next - this function is thin enough that any test would just ensure that it's called
export function setUserContext(userContext: SentryTypes.User) {
  Sentry.configureScope(scope => {
    scope.setUser(userContext);
  });
}

// Sets tags on the Sentry instance. Changes here will impact all events reported by the Sentry project.
// istanbul ignore next - this function is thin enough that any test would just ensure that it's called
export function setTags(tags: { [key: string]: string }) {
  Sentry.configureScope(scope => {
    scope.setTags(tags);
  });
}

// These are fields recognized by Sentry; everything else goes into the `data` object
const breadcrumbAttributes = ['category', 'level', 'type'];
export function addBreadcrumb(message: string, metadata: { [key: string]: string }) {
  const attributes = pick(metadata, breadcrumbAttributes);
  const data = omit(metadata, breadcrumbAttributes);

  Sentry.addBreadcrumb({
    message,
    ...attributes,
    data,
  });
}

// Exported for testing
export function generateBeforeSend(environment: string) {
  return (event: Sentry.Event) => {
    const allowedEnvironments = ['sandbox', 'beta', 'prod'];
    if (!includes(allowedEnvironments, environment)) {
      /* eslint-disable-next-line no-console */
      console.warn(`blocking notification because environment ${environment} is not allowed`);
      return null;
    }

    // Block any notifications from going through on URLs that aren't http. This blocks errors caused by users
    // downloading the app and trying to use it in a local filesystem (which results in URLs like file://C:/...)
    const requestUrlStartsWithHttp = event?.request?.url?.substring(0, 4) === 'http';
    if (!requestUrlStartsWithHttp) {
      return null;
    }

    return event;
  };
}

// Initializes Sentry with any provided opts.
export function initialize(initOpts: SentryTypes.Options) {
  // istanbul ignore next
  const { nodeEnv } = getConfig();
  /* eslint-disable-next-line no-console */
  console.warn(nodeEnv);
  Sentry.init({ beforeSend: generateBeforeSend(nodeEnv), environment: nodeEnv, ...initOpts });
  // Sentry groups by environment automatically, this is for ease of use in Sentry's search UI
  Sentry.setTags({ env: nodeEnv });
}

export interface SentryMetadata {
  [key: string]: any;
  fingerprint?: string[];
}

// exported for testing only
export function setMetadata(scope: Sentry.Scope, metadata: SentryMetadata) {
  const { fingerprint, ...extras } = metadata;
  scope.setExtras(extras);
  fingerprint && scope.setFingerprint(fingerprint);
}

export function captureException(error: Error, metadata: SentryMetadata) {
  Sentry.withScope(scope => {
    setMetadata(scope, metadata);
    Sentry.captureException(error);
  });
}
